<template>
  <div class="cmp-contact-form-os">
    <template v-if="!licenseOnly">
      <h6>
        {{ $t('product.properties.os.title') }}
      </h6>
      <ws-select
        v-model="model"
        class="q-mt-md"
        :options="options"
        option-value="type"
        :disable="!inEdit"
        :rules="[requiredOsValidator]"
      >
        <template
          v-for="slotName in ['selected', 'option']"
          :slot="slotName"
          slot-scope="scope"
        >
          <q-item
            :key="slotName + scope.index"
            v-bind="scope.itemProps"
            :class="{
              'q-pl-none': slotName === 'selected',
            }"
            v-on="scope.itemEvents"
          >
            <q-item-section>
              <q-item-label>
                <span
                  :class="{ 'text-ws-base100': !scope.opt && !model }"
                >{{ getOsLabel(scope) }}</span>
              </q-item-label>
            </q-item-section>
          </q-item>
        </template>
      </ws-select>
      <!-- <p v-else class="text-ws-base150 q-mt-md">
        {{
          $t(getKey('properties.os.' + model.type, 'label'), {
            _fallback: model.name,
            _silent: true,
          })
        }}
      </p> -->
    </template>
    <transition name="slide-fade-vertical">
      <div
        v-if="get(model, 'licences.length') || (!inEdit && licences.length)"
        class="licences"
      >
        <hr v-if="!licenseOnly" class="ws-default q-my-lg" />

        <h6 class="q-mt-2xl">
          {{ $t('product.properties.os.licences_title') }}
        </h6>

        <component
          :is="inEdit ? WsCheckbox : ListItemCmp"
          v-for="licence in inEdit ? model.licences : licences"
          :key="licence.type"
          v-model="licencesModel"
          :val="licence"
          :checked="licence.required"
          :disable="licence.required || !inEdit"
          class="q-mt-lg q-mb-sm text-bold"
          full-width
        >
          <template v-slot>
            <!-- standalone licence product -->
            <span
              v-if="licenseOnly"
              class="text-ws-base150"
              :class="{ ['text-normal']: !inEdit }"
            >{{ $t(getKey(licence.code, 'title')) }}</span>
            <span
              v-else
              class="text-ws-base150"
              :class="{ ['text-normal']: !inEdit }"
            >{{ getLicenseLabel(licence) }}</span> &nbsp;
            <price-period
              :plain="!licences.includes(licence)"
              :prices="licence.prices"
              :price="licence.price"
              individual
              :period="period"
            />
          </template>
        </component>
      </div>
    </transition>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { QItem, QItemLabel, QItemSection } from 'quasar';

import { getSnakedKeyFactory, simpleWrapperCmp } from '@odin/utils';
import WsInput from '@WS_Components/WsInput.vue';
import WsRadioList from '@/components/WsRadioList.vue';
import WsSelect from '@WS_Components/WsSelect.vue';
import WsCheckbox from '@WS_Components/WsCheckbox.vue';
import PricePeriod from '@/components/PricePeriod.vue';
import { useI18n } from '@odin/services';

@Component({
  components: {
    QItem,
    QItemLabel,
    QItemSection,
    WsInput,
    WsRadioList,
    WsSelect,
    PricePeriod,
  },
})
export default class PropertyOs extends Vue {
  @Prop({required: true})
  readonly value!: any;
  @Prop({default: () => []})
  readonly options!: any;
  @Prop({default: () => []})
  readonly licences!: any;
  @Prop({default: 12})
  readonly period!: number;
  @Prop(Boolean)
  readonly licenseOnly!: boolean;
  @Prop(Boolean)
  readonly inEdit!: boolean;

  WsCheckbox = WsCheckbox;
  ListItemCmp = simpleWrapperCmp();
  i18next = useI18n();
  $t = this.i18next.t;

  get model() {
    if (typeof this.value === 'string') {
      return { name: this.value };
    }
    return { ...this.value };
  }
  set model(value: any) {
    this.reEmit(value);
  }

  get licencesModel() {
    return [...this.licences];
  }
  set licencesModel(value: any) {
    this.reEmitLicences(value);
  }

  @Watch('model.type')
  onLicencesChange() {
    const licences = this.model?.licences || [];
    if (!licences.length) {
      // selected option without licences, clear licences in model
      this.reEmitLicences([]);
    } else {
      // otherwise preselect required if not selected already
      this.reEmitLicences(licences.filter((licence: any) => licence.required));
    }
  }

  requiredOsValidator(val: any) {
    return val?.type ? true : this.$t('validation.field.required');
  }

  reEmit(model: any) {
    this.$emit('input', model);
  }

  reEmitLicences(licences: any[]) {
    this.$emit('update:licences', licences);
  }

  getKey(prop: string, key: string): string {
    this.getKey = getSnakedKeyFactory('product');
    return this.getKey(prop, key);
  }

  getOsLabel(scope: any) {
    if (scope.opt || this.model.type || this.model.name) {

      const osTranslationKey = this.getKey(
        'properties.os.' + (scope.opt || this.model).type,
        'label'
      );

      return this.i18next.i18next.exists(osTranslationKey)
        ? this.$t(osTranslationKey)
        : (scope.opt || this.model).name;
    }

    return this.$t('product.properties.os.placeholder');
  }

  getLicenseLabel(licence: any) {
    const licenseKey = this.getKey('properties.os.' + licence.type, 'label');

    return this.i18next.i18next.exists(licenseKey)
      ? this.$t(licenseKey)
      : licence.name;
  }

}
</script>

<style lang="scss" scoped>
.cmp-price-period {
  float: right;
}

.ws-select::v-deep {
  .q-field__native {
    padding: 0;
  }
}
</style>
